import React from 'react';
import { Link } from 'react-router-dom';
import { Pencil as PencilIcon, Users as UsersIcon } from 'lucide-react';
import RowIconAction, { iconActionClassName } from 'src/app/admin/components/row-icon-action/RowIconAction';
import { navRoutesConfig } from 'src/shared/data/navRoutesConfig';
import { AdminViews } from 'src/routes/routesConfig';
import { Customer } from 'src/shared/types';

export type Organisation = Pick<Customer, 'customerId' | 'customerName'>;

type OrgRowActionsProps = {
    organisation: Organisation;
    /** Whether this user may rename, which the mutation would otherwise refuse. */
    canRename: boolean;
    onRename: (organisation: Organisation) => void;
};

/**
 * What can be done to one organisation from its row. The row itself opens the organisation, so
 * every action here stops propagation — otherwise both would run and the row would win.
 */
const OrgRowActions = ({ organisation, canRename, onRename }: OrgRowActionsProps) => {
    const { customerId, customerName } = organisation;

    return (
        <div className='flex items-center gap-1 shrink-0'>
            {canRename && (
                <RowIconAction label='Rename organisation'>
                    <button
                        type='button'
                        aria-label={`Rename ${customerName}`}
                        data-testid={`org-rename-${customerId}`}
                        className={iconActionClassName}
                        onClick={event => {
                            event.stopPropagation();
                            onRename(organisation);
                        }}
                    >
                        <PencilIcon size={16} className='shrink-0' />
                    </button>
                </RowIconAction>
            )}

            <RowIconAction label='Manage users'>
                <Link
                    to={navRoutesConfig[AdminViews.USER_MANAGEMENT]({ customerId })}
                    onClick={event => event.stopPropagation()}
                    aria-label='Manage users'
                    data-testid={`org-manage-users-${customerId}`}
                    className={iconActionClassName}
                >
                    <UsersIcon size={16} className='shrink-0' />
                </Link>
            </RowIconAction>
        </div>
    );
};

export default OrgRowActions;
